import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Grid,
  CircularProgress,
  createFilterOptions,
} from '@mui/material';
import { COUNTRIES_STATES_CITIES_JSON } from '../../utils/locations';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';

type City = { id: number; name: string; latitude: string; longitude: string };
type StateWithCities = { name: string; cities: City[] };

const filterOptions = createFilterOptions<string>({ limit: 7 });

export default function CitySelect({
  register,
  registerValue,
  setValue,
  selectedState,
  countryCode,
  originalCity,
  id,
  disabled,
}: {
  register: UseFormRegister<any>;
  registerValue: string;
  setValue: UseFormSetValue<any>;
  selectedState?: string;
  id: string;
  countryCode?: number;
  originalCity?: string;
  disabled?: boolean;
}) {
  const [allStateCities, setAllStateCities] = useState<StateWithCities[]>([]);
  const [options, setOptions]  = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
    
  useEffect(() => {
    if (!countryCode) return;

    setLoading(true);
    fetch(COUNTRIES_STATES_CITIES_JSON(countryCode))
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load states for country ${countryCode}`);
        return res.json() as Promise<StateWithCities[]>;
      })
      .then(data => {
        setAllStateCities(data);
        // reset any previously selected city only if original does not exist
        if (originalCity) {
          setInputValue(originalCity);
          setValue(registerValue, originalCity);
        } else {
          setInputValue('');
        }
      })
      .catch(err => {
        console.error(err);
        setAllStateCities([]);
      })
      .finally(() => setLoading(false));
  }, [countryCode, setValue]);

  useEffect(() => {
    if (selectedState) {
      const stateObj = allStateCities.find(s => s.name === selectedState);
      setOptions(stateObj ? stateObj.cities.map(c => c.name) : []);
    } else {
      setOptions([]);
    }
  }, [selectedState, allStateCities]);

  return (
    <Grid item xs={12} md={6}>
      <Autocomplete<string, false, false, true>
        freeSolo
        options={options}
        filterOptions={filterOptions}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, newInput) => {
          setInputValue(newInput);
          setValue(registerValue, newInput);
        }}
        onChange={(_, newValue) => {
          const val = newValue ?? '';
          setInputValue(val);
          setValue(registerValue, val);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...register(registerValue)}
            fullWidth
            id={id}
            margin="normal"
            label="City"
            required
            disabled={disabled}
            autoComplete="off"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Grid>
  );
}
